/**
 * StandTable — the single renderer for standings-shaped tables.
 *
 * Standings are NOT a MobileDynastyTable: the locked design is a fit-to-width
 * grid with two rows per team (a points row + a faintly-tinted "Total Stats"
 * row), medal/leader coloring, and a user-team gold tint — a structural
 * mismatch with the scroll-sticky player-stat primitive. This component is the
 * shared source of truth for that shape, consumed by both the standings page
 * (MobileLeagueStandings) and the League Home preview (MobileStandingsPreview),
 * which previously each carried a verbatim copy of this table + the team-action
 * modal.
 *
 * Token-compliant: all stripe/sticky colors read from mobileTableStyles; no
 * hardcoded table-treatment hex. Preserves the two-row group, the Total-Stats
 * row, medal/leader coloring, and the user-team gold tint.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, BarChart3, X } from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { mobileTableStyles, mobileModalStyle } from '../../../services/mobileStyleService';

import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';

const C = dynastyTheme.tokens.colors;

// Blend a team's primary color 25% into the row background to produce the
// user-team highlight tint. Defaults the foreground to the gold accent and the
// background to the canonical odd-row token.
export function blendHighlight(fgHex, bgHex) {
  const parse = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const fg = parse(fgHex || C.goldBase);
  const bg = parse(bgHex || mobileTableStyles.sticky.odd);
  const blend = (f, b) => Math.round(f * 0.25 + b * 0.75);
  const r = blend(fg[0], bg[0]), g = blend(fg[1], bg[1]), b = blend(fg[2], bg[2]);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const SortIndicator = ({ isActive, direction }) => {
  if (!isActive) return null;
  return (
    <span className="ml-1" style={{ fontSize: '8px' }}>
      {direction === 'desc' ? '▼' : '▲'}
    </span>
  );
};

// Shared standings sort (totalPoints / team_name / points_<cat> / stats_<cat>).
export const sortStandingsTeams = (teamsToSort, config) => {
  return [...teamsToSort].sort((a, b) => {
    let aValue, bValue;
    if (config.key === 'totalPoints') {
      aValue = a.totalPoints || a.total_points || 0;
      bValue = b.totalPoints || b.total_points || 0;
    } else if (config.key === 'team_name') {
      aValue = a.team_name || '';
      bValue = b.team_name || '';
    } else if (config.key.startsWith('points_')) {
      const cat = config.key.replace('points_', '');
      aValue = a.category_points?.[cat] || 0;
      bValue = b.category_points?.[cat] || 0;
    } else if (config.key.startsWith('stats_')) {
      const cat = config.key.replace('stats_', '');
      aValue = a.stats?.hitting?.[cat] ?? a.stats?.pitching?.[cat] ?? 0;
      bValue = b.stats?.hitting?.[cat] ?? b.stats?.pitching?.[cat] ?? 0;
    } else {
      aValue = 0;
      bValue = 0;
    }
    if (config.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  });
};

const formatStatValue = (value, cat) => {
  let displayValue = value || 0;
  if (typeof value === 'number') {
    if (['AVG', 'OBP', 'SLG', 'OPS', 'WHIP'].includes(cat)) {
      displayValue = formatBattingAvg(value);
    } else if (['ERA'].includes(cat)) {
      displayValue = formatERA(value);
    } else if (Number.isInteger(value)) {
      displayValue = value;
    } else {
      displayValue = value.toFixed(1);
    }
  }
  return displayValue;
};

const StandTable = ({
  data,
  sortConfig,
  onSort,
  userTeamId,
  userTeamPrimary,
  leagueId,
  maxHeight = '600px',
}) => {
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedTeam(null), 200);
  };
  const navigateToTeamPage = (targetPage) => {
    if (selectedTeam && leagueId) {
      navigate(`/leagues/${leagueId}/team`, {
        state: {
          targetPage,
          targetTeamId: selectedTeam.team_id,
          targetTeamName: selectedTeam.team_name,
        },
      });
      handleCloseModal();
    }
  };

  if (!data || !data.teams || data.teams.length === 0) {
    return (
      <div className="text-center py-8">
        <p className={dynastyTheme.classes.text.neutralLight}>Loading standings...</p>
      </div>
    );
  }

  const sortedTeams = sortStandingsTeams(data.teams, sortConfig);
  const hittingCategories = data.categories?.hitting || [];
  const pitchingCategories = data.categories?.pitching || [];
  const numTeams = data.teams.length;

  return (
    <>
      <style>{`.standtable-scroll::-webkit-scrollbar{display:none;}`}</style>
      <div
        className="overflow-x-auto overflow-y-auto standtable-scroll"
        style={{ maxHeight, paddingRight: '1px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <table className="w-full text-xs">
          <thead>
            <tr className={`${dynastyTheme.classes.bg.darkLighter}`}>
              <th className={`py-1 px-1 text-center ${dynastyTheme.classes.text.white} font-bold sticky left-0 z-10`} style={{ minWidth: '30px', maxWidth: '30px', width: '30px', backgroundColor: mobileTableStyles.sticky.header }}>
                RK
              </th>
              <th
                className={`py-1 px-2 text-left ${dynastyTheme.classes.text.white} font-bold sticky left-0 z-10 cursor-pointer hover:bg-neutral-800/50`}
                style={{ minWidth: '130px', maxWidth: '130px', width: '130px', left: '30px', backgroundColor: mobileTableStyles.sticky.header }}
                onClick={() => onSort('team_name')}
              >
                Team
                <SortIndicator isActive={sortConfig.key === 'team_name'} direction={sortConfig.direction} />
              </th>

              {/* Hitting Categories */}
              {hittingCategories.map(cat => (
                <th
                  key={cat}
                  className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[35px] cursor-pointer hover:bg-neutral-800/50`}
                  onClick={() => onSort(`points_${cat}`)}
                >
                  {cat}
                  <SortIndicator isActive={sortConfig.key === `points_${cat}`} direction={sortConfig.direction} />
                </th>
              ))}

              {/* Separator */}
              <th className="w-[2px] p-0 bg-neutral-600"></th>

              {/* Pitching Categories */}
              {pitchingCategories.map(cat => (
                <th
                  key={cat}
                  className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[35px] cursor-pointer hover:bg-neutral-800/50`}
                  onClick={() => onSort(`points_${cat}`)}
                >
                  {cat}
                  <SortIndicator isActive={sortConfig.key === `points_${cat}`} direction={sortConfig.direction} />
                </th>
              ))}

              <th
                className={`py-1 px-1 text-center ${dynastyTheme.classes.text.white} font-bold min-w-[40px] cursor-pointer hover:bg-neutral-800/50`}
                onClick={() => onSort('totalPoints')}
              >
                TOTAL
                <SortIndicator isActive={sortConfig.key === 'totalPoints'} direction={sortConfig.direction} />
              </th>
              <th className={`py-1 px-1 text-center ${dynastyTheme.classes.text.success} font-bold min-w-[35px]`}>
                +/-
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team, index) => {
              const totalPoints = team.totalPoints || team.total_points || 0;
              const leaderPoints = sortedTeams[0].totalPoints || sortedTeams[0].total_points || 0;

              // Per-team striping: points row dark, stats row faintly lighter.
              const pointsRowIndex = index * 2;
              const statsRowIndex = index * 2 + 1;
              const pointsRowBgColor = pointsRowIndex % 2 === 0 ? mobileTableStyles.sticky.odd : mobileTableStyles.sticky.header;
              const statsRowBgColor = statsRowIndex % 2 === 0 ? mobileTableStyles.sticky.odd : mobileTableStyles.sticky.header;

              const isUserTeam = team.team_id === userTeamId;
              const finalPointsRowBg = isUserTeam
                ? blendHighlight(userTeamPrimary, pointsRowBgColor)
                : pointsRowBgColor;
              const finalStatsRowBg = statsRowBgColor;

              const stickyPointsRowBg = isUserTeam ? finalPointsRowBg : pointsRowBgColor;
              const stickyStatsRowBg = statsRowBgColor;

              return (
                <React.Fragment key={team.team_id}>
                  {/* Points Row - No border (grid line only on stats row) */}
                  <tr style={{ backgroundColor: finalPointsRowBg }}>
                    <td
                      className={`py-0.5 px-1 text-center sticky left-0 z-10`}
                      style={{ backgroundColor: stickyPointsRowBg, minWidth: '30px', maxWidth: '30px', width: '30px', fontSize: '11px', color: dynastyTheme.tokens.colors.neutral[300] }}
                    >
                      {index + 1}
                    </td>
                    <td
                      className={`py-0.5 px-2 sticky z-10`}
                      onClick={() => handleTeamClick(team)}
                      style={{ cursor: 'pointer', backgroundColor: stickyPointsRowBg, minWidth: '130px', maxWidth: '130px', width: '130px', left: '30px' }}
                    >
                      <div className="flex items-center gap-1">
                        <span className={`${
                          isUserTeam
                            ? dynastyTheme.classes.text.primary + ' font-bold'
                            : dynastyTheme.classes.text.white + ' font-medium'
                        }`} style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {team.team_name}
                        </span>
                      </div>
                    </td>

                    {/* Hitting points */}
                    {hittingCategories.map(cat => {
                      const points = team.category_points?.[cat] || 0;
                      const isMax = points === numTeams;
                      const isMin = points === 1;
                      return (
                        <td key={cat} className={`py-0.5 px-1 text-center text-[11px] font-bold ${
                          isMax ? dynastyTheme.classes.text.success :
                          isMin ? dynastyTheme.classes.text.error :
                          dynastyTheme.classes.text.white
                        }`}>
                          {points}
                        </td>
                      );
                    })}

                    {/* Separator */}
                    <td className="w-[2px] p-0 bg-neutral-600"></td>

                    {/* Pitching points */}
                    {pitchingCategories.map(cat => {
                      const points = team.category_points?.[cat] || 0;
                      const isMax = points === numTeams;
                      const isMin = points === 1;
                      return (
                        <td key={cat} className={`py-0.5 px-1 text-center text-[11px] font-bold ${
                          isMax ? dynastyTheme.classes.text.success :
                          isMin ? dynastyTheme.classes.text.error :
                          dynastyTheme.classes.text.white
                        }`}>
                          {points}
                        </td>
                      );
                    })}

                    <td className={`py-0.5 px-1 text-center font-bold text-[12px] ${dynastyTheme.classes.text.primary}`}>
                      {totalPoints}
                    </td>
                    <td className={`py-0.5 px-1 text-center font-semibold text-[10px] ${
                      index === 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                    }`}>
                      {index === 0 ? '-' : `-${Math.abs(leaderPoints - totalPoints).toFixed(1)}`}
                    </td>
                  </tr>

                  {/* Stats Row - faintly tinted "Total Stats" row, grid line here */}
                  <tr
                    className={`border-b ${dynastyTheme.classes.border.neutral}`}
                    style={{ backgroundColor: finalStatsRowBg }}
                  >
                    <td
                      className={`py-0.5 px-1 text-center sticky left-0 z-10`}
                      style={{ backgroundColor: stickyStatsRowBg, minWidth: '30px', maxWidth: '30px', width: '30px' }}
                    >
                    </td>
                    <td className={`py-0.5 px-2 sticky z-10 ${dynastyTheme.classes.text.neutralLight}`} style={{ fontSize: '10px', fontWeight: '500', backgroundColor: stickyStatsRowBg, minWidth: '130px', maxWidth: '130px', width: '130px', left: '30px' }}>
                      <span className="pl-3" style={{ color: dynastyTheme.tokens.colors.neutral[300] }}>Total Stats</span>
                    </td>

                    {/* Hitting stats */}
                    {hittingCategories.map(cat => (
                      <td key={`${cat}-stats`} className={`py-0.5 px-1 text-center text-[9px] ${dynastyTheme.classes.text.neutralLight}`}>
                        {formatStatValue(team.stats?.hitting?.[cat], cat)}
                      </td>
                    ))}

                    {/* Separator */}
                    <td className="w-[2px] p-0 bg-neutral-600"></td>

                    {/* Pitching stats */}
                    {pitchingCategories.map(cat => (
                      <td key={`${cat}-stats`} className={`py-0.5 px-1 text-center text-[9px] ${dynastyTheme.classes.text.neutralLight}`}>
                        {formatStatValue(team.stats?.pitching?.[cat], cat)}
                      </td>
                    ))}

                    <td colSpan="2"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Team Action Modal */}
      {showModal && selectedTeam && (
        <>
          <div
            onClick={handleCloseModal}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: mobileModalStyle.backdrop, zIndex: 9998, animation: 'fadeIn 0.2s ease-out' }}
          />
          <div
            style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, backgroundColor: dynastyTheme.tokens.colors.neutral[800], borderRadius: '12px', border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`, boxShadow: `0 10px 40px ${withAlpha(C.black, 0.5)}`, minWidth: '280px', maxWidth: '90%', animation: 'slideUp 0.2s ease-out' }}
          >
            <div style={{ padding: '16px', borderBottom: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: dynastyTheme.tokens.colors.neutral[100], margin: 0 }}>
                {selectedTeam.team_name}
              </h3>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: dynastyTheme.tokens.colors.neutral[400], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div style={{ padding: '12px' }}>
              <button
                onClick={() => navigateToTeamPage('roster')}
                style={{ width: '100%', padding: '12px 16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'transparent', border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`, borderRadius: '8px', color: dynastyTheme.tokens.colors.neutral[100], fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.15s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = dynastyTheme.tokens.colors.primary + '20'; e.currentTarget.style.borderColor = dynastyTheme.tokens.colors.primary; e.currentTarget.style.color = dynastyTheme.tokens.colors.primary; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = dynastyTheme.tokens.colors.neutral[700]; e.currentTarget.style.color = dynastyTheme.tokens.colors.neutral[100]; }}
              >
                <Users className="w-4 h-4" />
                <span>View Roster</span>
              </button>
              <button
                onClick={() => navigateToTeamPage('stats')}
                style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'transparent', border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`, borderRadius: '8px', color: dynastyTheme.tokens.colors.neutral[100], fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.15s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = dynastyTheme.tokens.colors.primary + '20'; e.currentTarget.style.borderColor = dynastyTheme.tokens.colors.primary; e.currentTarget.style.color = dynastyTheme.tokens.colors.primary; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = dynastyTheme.tokens.colors.neutral[700]; e.currentTarget.style.color = dynastyTheme.tokens.colors.neutral[100]; }}
              >
                <BarChart3 className="w-4 h-4" />
                <span>View Stats</span>
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp {
              from { opacity: 0; transform: translate(-50%, -45%); }
              to { opacity: 1; transform: translate(-50%, -50%); }
            }
          `}</style>
        </>
      )}
    </>
  );
};

export default StandTable;
